import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * QUICK TEST (O'Neill Canada) -- "added to cart + complementary products" drawer
 * (active on all breakpoints; the drawer is full-width on mobile).
 *
 * On a cart add it opens a sidebar drawer (same size/position as the cart
 * drawer), confirms the item was added, and shows complementary product cards
 * read straight from the added product's
 * `shopify--discovery--product_recommendation.complementary_products` metafield.
 *
 * The cards are produced by sections/complementary-drawer.liquid, fetched in the
 * added product's context via the Section Rendering API on the product's own URL
 * (`/products/<handle>?section_id=complementary-drawer`) -- so the section can
 * read `product.metafields[...]` directly. No recommendations API involved.
 *
 * Reuses the theme's DialogComponent so open/close/scroll-lock/animation match
 * the cart drawer. Adds from inside this drawer are tagged source
 * 'complementary-drawer' so it doesn't reopen/swap its own content.
 *
 * @extends {DialogComponent}
 */
class ComplementaryDrawerComponent extends DialogComponent {
  /** @type {string | null} */
  #lastHandle = null;

  /** @type {'added' | 'look'} how the drawer was opened (drives the header bar text) */
  #mode = 'added';
  /** @type {boolean} true when the drawer is showing the cross-sell fallback content */
  #isCrossSell = false;
  /** @type {string | null} default lead/sub captured from the shell (restored for the look mode) */
  #defaultLead = null;
  #defaultSub = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handlePromoUpdate);
    document.addEventListener('click', this.#handleOpenLook);
    this.addEventListener('click', this.#handleAddClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handlePromoUpdate);
    document.removeEventListener('click', this.#handleOpenLook);
    this.removeEventListener('click', this.#handleAddClick);
  }

  /** Declarative on:click target for the close button. */
  close() {
    this.closeDialog();
  }

  /**
   * Always open scrolled to the top. The drawer body keeps its scroll position
   * between opens, and showModal() can scroll a focused element into view -- so we
   * reset before AND after opening (plus a frame later to beat focus-scroll).
   * Queries the element directly so it works regardless of ref wiring.
   * @param {...any} args
   */
  showDialog(...args) {
    const body = this.querySelector('.complementary-drawer__body');
    if (body instanceof HTMLElement) body.scrollTop = 0;
    const result = super.showDialog(...args);
    if (body instanceof HTMLElement) {
      body.scrollTop = 0;
      requestAnimationFrame(() => {
        body.scrollTop = 0;
      });
    }
    return result;
  }

  /**
   * Updates the spend-more progress banner on any cart change while the drawer is
   * open (including this drawer's own quick-adds). O'Neill Canada.
   * @param {CustomEvent<{ resource?: any }>} event
   */
  #handlePromoUpdate = async (event) => {
    if (!this.refs.dialog?.open) return;
    let cart = event?.detail?.resource;
    if (!cart || typeof cart.items_subtotal_price !== 'number') {
      try {
        cart = await (await fetch(`${this.dataset.cartUrl}.js`)).json();
      } catch {
        return;
      }
    }
    this.#updatePromo(cart);
  };

  /** Fetches the cart and refreshes the promo banner (used by the cart-trigger open flow). */
  #refreshPromo = async () => {
    try {
      const cart = await (await fetch(`${this.dataset.cartUrl}.js`)).json();
      this.#updatePromo(cart);
    } catch {
      /* no-op */
    }
  };

  /**
   * Renders the tiered spend-more progress banner from the cart subtotal: headlines
   * the nearest unlock, fills the bar, marks reached ticks, and shows ✓ chips for
   * unlocked tiers. Tier numbers come from theme settings, copy from locale files
   * (both baked into the [data-ctl-promo-config] JSON). Display only -- the actual
   * discounts live in Shopify. O'Neill Canada.
   * @param {any} cart
   */
  #updatePromo(cart) {
    const promo = /** @type {HTMLElement | undefined} */ (this.refs.promo);
    if (!promo) return;

    const configEl = promo.querySelector('[data-ctl-promo-config]');
    let config;
    try {
      config = JSON.parse(configEl?.textContent || '{}');
    } catch {
      promo.hidden = true;
      return;
    }

    const tiers = Array.isArray(config.tiers)
      ? config.tiers.filter(Boolean).slice().sort((a, b) => a.threshold - b.threshold)
      : [];
    if (!tiers.length) {
      promo.hidden = true;
      return;
    }

    const subtotal = Number.isFinite(cart?.items_subtotal_price)
      ? cart.items_subtotal_price
      : Number.isFinite(cart?.total_price)
        ? cart.total_price
        : 0;
    const currency = cart?.currency || 'USD';
    const maxThreshold = tiers[tiers.length - 1].threshold || 1;
    const nextTier = tiers.find((tier) => subtotal < tier.threshold) || null;

    let headlineText = '';
    if (nextTier) {
      const remaining = Math.max(nextTier.threshold - subtotal, 0);
      headlineText = String(nextTier.headline || '').replace('%AMOUNT%', this.#formatMoney(remaining, currency));
      if (nextTier.reward != null) {
        headlineText = headlineText.replace('%REWARD%', this.#formatMoney(nextTier.reward, currency, true));
      }
    } else {
      headlineText = config.allUnlocked || '';
    }

    const headline = /** @type {HTMLElement | undefined} */ (this.refs.promoHeadline);
    // The headline change-cue animation is handled globally by assets/promo-cue.js
    // (a shared MutationObserver, so the cart banner and this one behave identically).
    if (headline) headline.textContent = headlineText;

    // Cross-sell fallback drawer keeps its localized "Add more, save more" header
    // (set in #applyHeader from data-lead); the live promo line stays in the banner below.

    const fill = /** @type {HTMLElement | undefined} */ (this.refs.promoFill);
    if (fill) fill.style.width = `${Math.min(subtotal / maxThreshold, 1) * 100}%`;

    promo.classList.toggle('is-complete', !nextTier);

    const ticks = /** @type {HTMLElement | undefined} */ (this.refs.promoTicks);
    if (ticks) {
      ticks.replaceChildren();
      for (const tier of tiers) {
        const tick = document.createElement('span');
        tick.className = `ctl-promo__tick${subtotal >= tier.threshold ? ' is-reached' : ''}`;
        tick.style.insetInlineStart = `${Math.min(tier.threshold / maxThreshold, 1) * 100}%`;
        ticks.appendChild(tick);
      }
    }

    const chips = /** @type {HTMLElement | undefined} */ (this.refs.promoChips);
    if (chips) {
      chips.replaceChildren();
      // Once the top tier is unlocked, its bundle supersedes the lower tiers (e.g.
      // "$25 off + free express" replaces "free shipping" and "$15 off"), so show only
      // the top badge instead of all three. Otherwise show every tier (locked ones
      // greyed). tiers is sorted ascending, so the last entry is the top tier. O'Neill.
      const chipTiers = nextTier ? tiers : tiers.slice(-1);
      for (const tier of chipTiers) {
        const chip = document.createElement('span');
        // Earned tiers get the green outline + check via the .is-done class.
        chip.className = `ctl-promo__chip${subtotal >= tier.threshold ? ' is-done' : ''}`;
        let chipText = String(tier.chip || '');
        if (tier.reward != null) chipText = chipText.replace('%REWARD%', this.#formatMoney(tier.reward, currency, true));
        chip.textContent = chipText;
        chips.appendChild(chip);
      }
    }

    promo.hidden = false;
  }

  /**
   * Formats a cents amount in the shopper's locale and the cart's currency.
   * @param {number} cents
   * @param {string} currency
   * @returns {string}
   */
  #formatMoney(cents, currency, trimZeros = false) {
    const amount = (Number(cents) || 0) / 100;
    const locale = document.documentElement.lang || 'en';
    /** @type {Intl.NumberFormatOptions} */
    const options = { style: 'currency', currency, currencyDisplay: 'narrowSymbol' };
    // For round promo amounts ($15) drop the ".00" so it reads "15 $" / "$15".
    if (trimZeros && Number.isInteger(amount)) {
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
    }
    try {
      return new Intl.NumberFormat(locale, options).format(amount);
    } catch {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
      } catch {
        return amount.toFixed(2);
      }
    }
  }

  /**
   * Sets the drawer header for the two entry points.
   * @param {'added' | 'look'} mode
   */
  #setHeader(mode) {
    this.#mode = mode;
    const added = mode === 'added';
    const text = /** @type {HTMLElement | undefined} */ (this.refs.confirmText);
    const check = /** @type {HTMLElement | undefined} */ (this.refs.confirmCheck);
    const hanger = /** @type {HTMLElement | undefined} */ (this.refs.confirmHanger);
    const lead = /** @type {HTMLElement | undefined} */ (this.refs.leadTitle);
    if (text)
      text.textContent = added
        ? this.dataset.labelAdded || Theme?.translations?.added_to_cart || 'Added to cart'
        : this.dataset.labelLook || 'Complete the look';
    if (check) check.hidden = !added;
    if (hanger) hanger.hidden = added;
    // "Complete the look" body lead only on the add flow (the cart flow already
    // shows it in the header).
    if (lead) lead.hidden = !added;
  }

  /**
   * Opens the drawer for a product from a "Complete the look" trigger on a cart
   * line item (works from the cart page or the mini-cart drawer, any page).
   * @param {MouseEvent} event
   */
  #handleOpenLook = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-complementary-open]');
    if (!trigger) return;

    const handle = trigger.getAttribute('data-handle');
    const url = trigger.getAttribute('data-url');
    if (!handle || !url) return;

    event.preventDefault();

    // If the trigger came from the mini-cart drawer, close it so we don't stack
    // two modal dialogs (which breaks the shared body scroll-lock).
    const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    const closing =
      cartDrawer?.querySelector('dialog')?.open && typeof cartDrawer.closeDialog === 'function'
        ? cartDrawer.closeDialog()
        : Promise.resolve();

    this.#setHeader('look');
    await Promise.all([this.#loadComplementary({ handle, url }), closing]);
    this.showDialog();
    this.#refreshPromo();
  };

  /**
   * @param {CustomEvent<{ resource?: any, data?: { productId?: string, source?: string } }>} event
   */
  #handleCartAdd = async (event) => {
    const data = event.detail?.data ?? {};

    // Ignore adds that originate from inside this drawer (avoid a reopen loop).
    if (data.source === 'complementary-drawer') return;
    // Ignore adds while the drawer is already open (e.g. adding a complementary
    // item via its quick-add modal) so it doesn't swap content mid-interaction.
    if (this.refs.dialog?.open) return;

    const productId = data.productId;
    if (!productId) return;

    // Find the added line item so we know its handle/url and can confirm it.
    let cart = event.detail?.resource;
    let item = this.#findItem(cart, productId);
    if (!item) {
      try {
        cart = await (await fetch(`${this.dataset.cartUrl}.js`)).json();
        item = this.#findItem(cart, productId);
      } catch (error) {
        console.error('Complementary drawer (cart fetch):', error);
      }
    }

    this.#setHeader('added');
    await this.#loadComplementary(item);
    this.showDialog();
    this.#updatePromo(cart);
  };

  /**
   * @param {any} cart
   * @param {string | number} productId
   * @returns {any | null}
   */
  #findItem(cart, productId) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.filter((i) => String(i.product_id) === String(productId)).pop() ?? null;
  }

  /**
   * Fetches the complementary section in the added product's context and injects
   * its cards. Uses the line item's own URL so any locale prefix is preserved.
   * @param {any | null} item
   */
  async #loadComplementary(item) {
    const content = /** @type {HTMLElement | undefined} */ (this.refs.content);
    if (!content) return;

    const handle = item?.handle;
    const itemUrl = item?.url;
    if (!handle || !itemUrl) {
      content.innerHTML = '';
      this.#toggleSuggestions(content);
      return;
    }

    // Simple cache: same product added twice in a row -> keep existing cards.
    if (this.#lastHandle === handle && content.children.length) {
      this.#toggleSuggestions(content);
      return;
    }

    // The Ajax cart's item.url is root-relative with no locale prefix, so fetching it
    // directly renders the section in the default locale (English) -- its localized
    // strings (e.g. the "Add more, save more" lead) come back untranslated. Prefix the
    // current locale/market root so the section renders in the shopper's locale.
    const path = itemUrl.split('?')[0];
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const prefix = root === '/' ? '' : root.replace(/\/$/, '');
    const localizedPath = prefix && !path.startsWith(`${prefix}/`) ? `${prefix}${path}` : path;
    const url = `${localizedPath}?section_id=${this.dataset.sectionId}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const body = doc.querySelector('[data-complementary-content]');
      content.innerHTML = body ? body.innerHTML : '';
      this.#applyHeader(body);
      this.#lastHandle = handle;
    } catch (error) {
      console.error('Complementary drawer:', error);
      content.innerHTML = '';
    }

    this.#buildCardDots(content);
    this.#toggleSuggestions(content);
  }

  /**
   * Adds swipe-indicator dots to each companion card's image gallery and keeps the
   * active dot in sync with the gallery scroll position. Shown on mobile (CSS);
   * on desktop the cards use hover arrows instead. O'Neill Canada.
   * @param {HTMLElement} content
   */
  #buildCardDots(content) {
    for (const gallery of content.querySelectorAll('.complementary-card__gallery')) {
      if (!(gallery instanceof HTMLElement)) continue;

      const slides = gallery.querySelectorAll('.complementary-card__slide');
      const media = gallery.closest('.complementary-card__media');
      if (slides.length <= 1 || !media || media.querySelector('.complementary-card__dots')) continue;

      const dots = document.createElement('div');
      dots.className = 'complementary-card__dots';
      /** @type {HTMLElement[]} */
      const dotEls = [];
      for (let i = 0; i < slides.length; i++) {
        const dot = document.createElement('span');
        dot.className = 'complementary-card__dot';
        dots.appendChild(dot);
        dotEls.push(dot);
      }
      media.appendChild(dots);

      let raf = 0;
      const setActive = () => {
        const width = gallery.clientWidth || 1;
        const index = Math.round(gallery.scrollLeft / width);
        dotEls.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      gallery.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          setActive();
        });
      });
      setActive();
    }
  }

  /**
   * Swaps the drawer header + sub-header to match the injected content. The
   * cross-sell fallback (data-cards-only) provides localized data-lead / data-sub
   * ("Add more, save more" / "Frequently bought together"); otherwise the default
   * "Complete the look" / "Handpicked outfit..." from the shell is restored.
   * @param {Element | null} body - the fetched [data-complementary-content] element
   */
  #applyHeader(body) {
    const lead = /** @type {HTMLElement | undefined} */ (this.refs.leadTitle);
    const sub = /** @type {HTMLElement | undefined} */ (this.refs.subTitle);
    const confirm = /** @type {HTMLElement | undefined} */ (this.refs.confirmText);

    // Capture the shell defaults once (before we ever override them).
    if (this.#defaultLead == null && lead) this.#defaultLead = lead.textContent;
    if (this.#defaultSub == null && sub) this.#defaultSub = sub.textContent;

    const crossSell = body instanceof HTMLElement && body.dataset.cardsOnly === 'true';
    this.#isCrossSell = crossSell;
    const leadText = crossSell ? (body.dataset.lead || this.#defaultLead) : this.#defaultLead;
    const subText = crossSell ? (body.dataset.sub || this.#defaultSub) : this.#defaultSub;

    if (lead && leadText != null) lead.textContent = leadText;
    if (sub && subText != null) sub.textContent = subText;
    // In the cart "look" flow the header bar (confirmText) carries this text, not the lead.
    if (confirm && this.#mode === 'look' && leadText != null) confirm.textContent = leadText;
  }

  /**
   * Hides the "You might also like" heading when there are no cards to show.
   * @param {HTMLElement} content
   */
  #toggleSuggestions(content) {
    const hasCards = Boolean(content.querySelector('.complementary-outfit, [data-add-variant]'));
    this.toggleAttribute('data-has-suggestions', hasCards);
  }

  /**
   * Adds a complementary product to the cart from within the drawer.
   * @param {MouseEvent} event
   */
  #handleAddClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // "View cart": close this modal and open the theme's mini-cart drawer instead
    // of navigating to the cart page. Falls back to the link's href if there is no
    // cart drawer on the page (e.g. cart type is a page).
    const viewCart = target.closest('[data-view-cart]');
    if (viewCart) {
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        event.preventDefault();
        this.closeDialog().then(() => cartDrawer.open());
      }
      return;
    }

    // Product-card image carousel arrows -- wrap around at either end so both
    // arrows always advance (prev from the first image jumps to the last).
    const arrow = target.closest('[data-gallery-prev], [data-gallery-next]');
    if (arrow) {
      event.preventDefault();
      const gallery = arrow.parentElement?.querySelector('[data-gallery]');
      if (gallery instanceof HTMLElement) {
        const slideWidth = gallery.clientWidth || 1;
        const count = gallery.children.length;
        if (count > 1) {
          const current = Math.round(gallery.scrollLeft / slideWidth);
          const step = arrow.hasAttribute('data-gallery-next') ? 1 : -1;
          const nextIndex = (current + step + count) % count;
          gallery.scrollTo({ left: nextIndex * slideWidth, behavior: 'smooth' });
        }
      }
      return;
    }

    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-add-variant]'));
    if (!button || button.disabled) return;

    const variantId = button.dataset.addVariant;
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = Theme?.translations?.adding ?? 'Adding…';

    try {
      await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      // Fetch the fresh cart so the bubble can be set to the correct total.
      const cart = await (await fetch(`${this.dataset.cartUrl}.js`)).json();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'complementary-drawer',
          itemCount: cart.item_count,
        })
      );

      button.textContent = Theme?.translations?.added ?? 'Added ✓';
      button.setAttribute('data-added', '');
    } catch (error) {
      console.error('Complementary drawer add:', error);
      button.textContent = originalText;
      button.disabled = false;
    }
  };
}

if (!customElements.get('complementary-drawer-component')) {
  customElements.define('complementary-drawer-component', ComplementaryDrawerComponent);
}
